/**
 * Tenant Service - Multi-tenant configuration management
 *
 * Handles subdomain extraction from URLs and fetches tenant configuration from the gateway.
 * Provides caching mechanisms and helper functions for accessing tenant-specific data.
 * Supports both development (localhost) and production environments with subdomain-based routing.
 * Manages Firebase tenant pool configuration and corporate information for multi-tenant apps.
 * Updated: 2025-12-23 - Fixed API URLs to include /graphql endpoint
 */

import { IS_PROTOTYPE } from '@hapana/shared/web/services/prototype-mock/config'

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL || 'http://localhost:3002'

// Cache TTL: 24 hours in milliseconds (tenant config rarely changes)
const TENANT_CONFIG_TTL_MS = 24 * 60 * 60 * 1000

export const extractSubdomain = () => {
  const hostname = window.location.hostname

  // ponytail: the backend-free prototype has no tenant-config gateway, so any non-localhost
  // host (e.g. *.pages.dev) extracts a real subdomain and the fetch fails -> "Brand not found".
  // Force null everywhere to match the proven localhost path (no fetch, no error).
  if (IS_PROTOTYPE) {
    return null
  }

  // TEMPORARY: Hardcode subdomain to "elite" for dev environment
  // TODO: Remove this when proper subdomain routing is configured
  if (hostname.includes('run.app') || hostname.includes('cloudrun')) {
    return 'elite'
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // In development without subdomain, return null or a default
    return import.meta.env.VITE_DEFAULT_SUBDOMAIN || null
  }

  const parts = hostname.split('.')

  if (parts.length >= 2) {
    if (parts[parts.length - 1] === 'localhost') {
      const subdomain = parts[0]
      return subdomain
    }

    const subdomain = parts[0]
    if (subdomain !== 'www' && subdomain !== 'hapana') {
      return subdomain
    }
  }

  return null
}

/**
 * Save tenant config to localStorage with TTL for persistence across sessions.
 *
 * NOTE: We intentionally use both localStorage and sessionStorage for tenant config:
 * - localStorage: Primary cache with TTL - persists across sessions/tabs for faster
 *   page loads when users return (eliminates ~200ms tenant config fetch on return visits)
 * - sessionStorage: Backward compatibility - some legacy code may read from sessionStorage
 *   directly, so we keep it in sync to avoid breaking changes
 *
 * This dual-storage approach will be cleaned up once all code uses getCachedTenantConfig().
 */
const saveTenantConfigToStorage = (subdomain, data) => {
  try {
    const cacheEntry = {
      data,
      subdomain,
      cachedAt: Date.now(),
    }
    localStorage.setItem('tenantConfig', JSON.stringify(cacheEntry))
    // sessionStorage kept for backward compatibility with legacy code
    sessionStorage.setItem('tenantConfig', JSON.stringify(data))
    sessionStorage.setItem('currentSubdomain', subdomain)
  } catch (_) {
    // Continue anyway - not critical if caching fails
  }
}

/**
 * Get tenant config from localStorage if valid (not expired and matching subdomain)
 */
const getValidCachedConfig = (subdomain) => {
  try {
    const cached = localStorage.getItem('tenantConfig')
    if (!cached) return null

    const cacheEntry = JSON.parse(cached)

    // Check if cache is for the same subdomain
    if (cacheEntry.subdomain !== subdomain) return null

    // Check if cache is still valid (within TTL)
    const age = Date.now() - cacheEntry.cachedAt
    if (age > TENANT_CONFIG_TTL_MS) {
      // Cache expired, clear it
      localStorage.removeItem('tenantConfig')
      return null
    }

    return cacheEntry.data
  } catch (_) {
    return null
  }
}

export const fetchTenantConfig = async (subdomain, signal) => {
  if (!subdomain) {
    throw new Error('Subdomain is required to fetch tenant configuration')
  }

  const response = await fetch(`${GATEWAY_URL}/api/tenant-config/${subdomain}`, {
    signal,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch tenant config: ${response.status}`)
  }

  const data = await response.json()

  // Save to both localStorage (persistent) and sessionStorage (backward compat)
  saveTenantConfigToStorage(subdomain, data)

  return data
}

export const getCachedTenantConfig = () => {
  const subdomain = extractSubdomain()

  // First try localStorage with TTL check
  if (subdomain) {
    const persistentCache = getValidCachedConfig(subdomain)
    if (persistentCache) {
      return persistentCache
    }
  }

  // Fall back to sessionStorage for backward compatibility
  try {
    const cached = sessionStorage.getItem('tenantConfig')
    return cached ? JSON.parse(cached) : null
  } catch (_) {
    return null
  }
}

export const getStaffTenantId = () => {
  const config = getCachedTenantConfig()
  return config?.tenants?.staff || null
}

export const getClientTenantId = () => {
  const config = getCachedTenantConfig()
  return config?.tenants?.client || null
}

export const getCorporateInfo = () => {
  const config = getCachedTenantConfig()
  return config?.corporate || null
}

export const clearTenantConfig = () => {
  localStorage.removeItem('tenantConfig')
  sessionStorage.removeItem('tenantConfig')
  sessionStorage.removeItem('currentSubdomain')
}

export const initializeTenant = async () => {
  const subdomain = extractSubdomain()

  if (!subdomain) {
    return null
  }

  // Check persistent cache first (localStorage with TTL)
  const persistentCache = getValidCachedConfig(subdomain)
  if (persistentCache) {
    // Also populate sessionStorage for backward compatibility
    try {
      sessionStorage.setItem('tenantConfig', JSON.stringify(persistentCache))
      sessionStorage.setItem('currentSubdomain', subdomain)
    } catch (_) {}
    return persistentCache
  }

  // Fall back to sessionStorage check
  try {
    const cachedSubdomain = sessionStorage.getItem('currentSubdomain')
    if (cachedSubdomain === subdomain) {
      const cached = getCachedTenantConfig()
      if (cached) {
        return cached
      }
    }
  } catch (_) {
    // Continue to fetch fresh config
  }

  return fetchTenantConfig(subdomain)
}

export const getTenantUserPool = async (tenant) => {
  try {
    const config = await fetchTenantConfig(tenant)
    return {
      success: true,
      tenants: config.tenants,
      corporate: config.corporate,
    }
  } catch (_) {
    return null
  }
}
